/**
 * The always-on frame and boundary HUD.
 *
 * The roadmap wants this live from B0.1 rather than from B0.6, so that a
 * performance regression is caught in the package that caused it. Everything
 * here is therefore a *number*, not a graph: what a frame costs, split three
 * ways; what the boundary is moving, in bytes and publishes; what the worker's
 * own step costs; and which transport is underneath, because the answer to
 * "does the boundary stay quiet under stress" differs between them.
 *
 * The controls below it are the B0.1 command set — the round trip made
 * clickable. They exist to prove the queue, not to be a UI.
 */

import { useSyncExternalStore } from 'react';
import type { FloorHost, ToolLoadUi } from '../bridge';
import { snapshot, subscribe, type HudState } from './store';

export function Hud({ host }: { host: FloorHost }) {
  const state = useSyncExternalStore(subscribe, snapshot);
  if (state.error) {
    return (
      <div className="hud hud-error">
        <h2>The sim worker stopped</h2>
        <p>{state.error}</p>
      </div>
    );
  }
  return (
    <>
      <div className="hud">
        <Frames state={state} />
        <Boundary state={state} />
        <Sim state={state} />
        <SitePanel state={state} />
        <FlowPanel state={state} />
        <CrewPanel state={state} />
      </div>
      <Controls host={host} state={state} />
    </>
  );
}

/**
 * What is built, what it cost, and the layout hash — which is on screen rather
 * than only in a test because B0.2's done-when is that the hash survives a
 * round trip through a file, and a number you can watch is a number you notice
 * moving.
 */
function SitePanel({ state }: { state: HudState }) {
  const site = state.ui?.site;
  if (!site) return null;
  const tight = site.utilityDemand > site.utilitySupply * 0.9;
  return (
    <section>
      <h3>Site</h3>
      <Row label="catalog" value={`${site.catalogId} · ${site.era}`} />
      <Row label="plot" value={`${site.plotW}×${site.plotH} cells`} />
      <Row label="built" value={`${site.rooms} rooms · ${site.tools} tools`} />
      <Row label="cleanroom" value={`${site.cleanCells.toLocaleString()} cells`} />
      <Row
        label="utility"
        value={`${site.utilityDemand} / ${site.utilitySupply} units`}
        tone={tight ? 'warn' : 'ok'}
      />
      <Row label="capital" value={money(site.buildCostDollars)} />
      <Row label="upkeep" value={`${money(site.weeklyUpkeepDollars)}/wk`} />
      <Row label="refusals" value={String(site.refusals)} tone={site.refusals > 0 ? 'warn' : undefined} />
      <Row label="layout hash" value={site.layoutHash} mono />
    </section>
  );
}

/**
 * The flow: what is circulating, how much of it gets out, and which tool is the
 * reason it does not get out faster.
 *
 * The X-factor row is the one that matters — cycle time over the pure process
 * time a lot needs. It reads about 1.2 on an easy floor and climbs past 5 as
 * the bottleneck saturates, which is the whole lesson B0.3 exists to teach, on
 * screen rather than only in the harness.
 */
function FlowPanel({ state }: { state: HudState }) {
  const flow = state.ui?.flow;
  if (!flow) return null;
  const bottleneck = flow.bottleneck;
  const busy = (bottleneck?.recentMille ?? 0) / 10;
  return (
    <section>
      <h3>Flow</h3>
      <Row label="recipe" value={`${flow.steps} steps · ${flow.loops} loops`} />
      <Row label="starts" value={`${flow.startsPerDay}/day · ${flow.wafersPerLot} wafers a lot`} />
      <Row
        label="WIP"
        value={`${flow.inFlight} lots · ${flow.wipWafers.toLocaleString()} wafers`}
      />
      <Row label="out" value={`${flow.completed} lots · ${flow.wafersOutPerWeek.toLocaleString()} wafers/wk`} />
      <Row label="cycle" value={`${flow.meanCycleHoursAll.toFixed(1)} h`} />
      <Row
        label="of which walking"
        value={
          flow.transitHours > 0
            ? `${flow.transitHours.toFixed(1)} h · ${(flow.transitShareMille / 10).toFixed(1)}%`
            : '—'
        }
      />
      <Row
        label="X-factor"
        value={flow.cycleRatio > 0 ? `${flow.cycleRatio.toFixed(2)}×` : '—'}
        tone={flow.cycleRatio >= 3 ? 'over' : flow.cycleRatio >= 2 ? 'warn' : 'ok'}
      />
      <Row
        label="bottleneck"
        value={bottleneck ? `${bottleneck.name} · ${busy.toFixed(0)}%` : '—'}
        tone={busy >= 95 ? 'over' : busy >= 80 ? 'warn' : undefined}
      />
      <Row
        label="deepest queue"
        value={deepest(flow.tools)}
      />
      {flow.awaitingCarriage > 0 ? (
        <Row
          label="awaiting a carrier"
          value={`${flow.awaitingCarriage} lots`}
          tone={flow.awaitingCarriage > 4 ? 'over' : 'warn'}
        />
      ) : null}
      {flow.starvedTools > 0 ? (
        <Row
          label="nobody at"
          value={`${flow.starvedTools} tools`}
          tone="over"
        />
      ) : null}
      {flow.hotLots > 0 ? <Row label="expedited" value={`${flow.hotLots} hot lots`} tone="warn" /> : null}
      {flow.stuck > 0 ? (
        <Row label="stuck" value={`${flow.stuck} lots — a step has no tool`} tone="over" />
      ) : null}
    </section>
  );
}

/**
 * The crew: who is on the floor, what they are doing, and the two prices the
 * needs charge.
 *
 * The discipline row is the one with a future. Cutting the gowning ritual is
 * where B0.5's D₀ modifier will come from ([production](docs/06-production.md)),
 * so it is on screen from this package — before it is wired to anything — for
 * the same reason the layout hash was: a number you can watch move is a
 * different thing from a number in a test.
 */
function CrewPanel({ state }: { state: HudState }) {
  const crew = state.ui?.crew;
  if (!crew) return null;
  const fatigue = crew.fatigueMille / 10;
  const discipline = crew.disciplineMille / 10;
  const working = crew.carrying + crew.loading + crew.repairing;
  return (
    <section>
      <h3>Crew</h3>
      <Row
        label="on shift"
        value={crew.roles.map((role) => `${role.count} ${role.id}`).join(' · ')}
      />
      <Row
        label="working"
        value={
          crew.offDuty
            ? `off shift — ${crew.shifts} × 8 h pattern, nobody on the floor`
            : `${working} of ${crew.agents} · ${crew.walking} walking · ${crew.idle} idle`
        }
        tone={crew.agents > 0 && working === 0 && !crew.offDuty ? 'warn' : undefined}
      />
      <Row
        label="fatigue"
        value={`${fatigue.toFixed(0)}% · ${crew.resting} on a break`}
        tone={fatigue >= 80 ? 'over' : fatigue >= 60 ? 'warn' : 'ok'}
      />
      <Row
        label="gowning"
        value={`${crew.gowning} in the airlock · ${crew.gowningHours.toFixed(1)} h spent`}
      />
      <Row
        label="discipline"
        value={`${discipline.toFixed(1)}% · ${crew.cuts} of ${crew.gownings} hurried`}
        tone={discipline < 80 ? 'over' : discipline < 95 ? 'warn' : 'ok'}
      />
      <Row
        label="contamination"
        value={`${crew.contamination} events (${crew.slips} fumbled loads)`}
        tone={crew.contamination > 0 ? 'warn' : 'ok'}
      />
      <Row label="carried" value={`${crew.deliveries} lots · ${crew.loads} runs started`} />
      <Row
        label="walking pace"
        value={crew.paceMille === 1000 ? '0.18 m/s (as shipped)' : `${(crew.paceMille / 1000).toFixed(1)}×`}
      />
    </section>
  );
}

/** The longest queue on the floor, named. Where a player should look first. */
function deepest(tools: ToolLoadUi[]): string {
  let best: ToolLoadUi | undefined;
  for (const tool of tools) {
    if (!best || tool.queueLots > best.queueLots) best = tool;
  }
  if (!best || best.queueLots === 0) return 'nothing waiting';
  return `${best.queueLots} lots at the ${best.name.toLowerCase()}`;
}

function money(dollars: number): string {
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(2)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(1)}k`;
  return `$${dollars.toFixed(0)}`;
}

/**
 * The frame, against the budget (B0.6).
 *
 * Every threshold below comes from `state.budget`, which the *sim* stated and
 * handed over at boot — there is no number in this file. That is the difference
 * between the panel B0.1 shipped and this one: the old one reported, and a
 * reader had to know what good looked like. This one says.
 *
 * The line that matters is `main thread`, not `frame`. Frame time is an interval
 * and includes everything the browser does between callbacks; main-thread JS is
 * what this build actually spends, and it is the half the roadmap's target names.
 */
function Frames({ state }: { state: HudState }) {
  const budget = state.budget;
  const frameTone = !budget
    ? undefined
    : state.fps < budget.targetFps * 0.95
      ? 'over'
      : 'ok';
  const scriptTone = !budget
    ? undefined
    : state.scriptMs * 1000 > budget.mainThreadUs
      ? 'over'
      : state.scriptMs * 1000 > budget.mainThreadUs / 2
        ? 'warn'
        : 'ok';
  // What the callback spends outside the two named parts: the heat upload, the
  // lot loop, the ghost. Named because a budget with an unexplained remainder is
  // a budget nobody will trust.
  const other = Math.max(0, state.scriptMs - state.renderMs - state.boundaryMs);
  return (
    <section>
      <h3>Frame</h3>
      <Row
        label="fps"
        value={budget ? `${state.fps.toFixed(0)} / ${budget.targetFps}` : state.fps.toFixed(0)}
        tone={frameTone}
      />
      <Row
        label="frame"
        value={`${state.frameMs.toFixed(2)} ms · p95 ${state.frameMsP95.toFixed(2)}`}
        tone={frameTone}
      />
      <Row
        label="main thread"
        value={
          budget
            ? `${state.scriptMs.toFixed(2)} / ${(budget.mainThreadUs / 1000).toFixed(2)} ms · p95 ${state.scriptMsP95.toFixed(2)}`
            : `${state.scriptMs.toFixed(2)} ms`
        }
        tone={scriptTone}
      />
      <Row label="of which render" value={`${state.renderMs.toFixed(2)} ms`} />
      <Row label="of which boundary" value={`${state.boundaryMs.toFixed(2)} ms`} />
      <Row label="of which the rest" value={`${other.toFixed(2)} ms`} />
      <Row
        label="long tasks"
        value={
          state.longTasksSupported
            ? `${state.longTasks.toFixed(1)}/s · worst ${state.longestTaskMs.toFixed(0)} ms`
            : 'not reported by this browser'
        }
        tone={state.longTasksSupported && state.longTasks > 1 ? 'warn' : undefined}
      />
      <Row
        label="instances"
        value={`${state.drawn.toLocaleString()} + ${state.structures} built + ${state.lots} lots`}
      />
      <Row
        label="crowd LOD"
        value={
          state.lod
            ? state.lod.enabled
              ? `detail ${state.lod.detailRadius.toFixed(0)} m · cull ${state.lod.cullRadius.toFixed(0)} m`
              : 'off — the control case'
            : '—'
        }
      />
      <Row label="interp α" value={state.alpha.toFixed(2)} />
    </section>
  );
}

function Boundary({ state }: { state: HudState }) {
  const transport =
    state.transport === 'sab' ? 'SharedArrayBuffer (views)' : 'postMessage (copy)';
  const budget = state.budget;
  const rateTone = !budget
    ? undefined
    : state.publishHz > 0 && Math.abs(state.publishHz - budget.publishHz) > budget.publishHz * 0.1
      ? 'over'
      : 'ok';
  const bytesTone = !budget
    ? undefined
    : state.bytesPerSecond / 1024 > budget.boundaryKbPerSecond
      ? 'over'
      : 'ok';
  return (
    <section>
      <h3>Boundary</h3>
      <Row label="transport" value={transport} tone={state.transport === 'sab' ? 'ok' : 'warn'} />
      <Row label="isolated" value={state.isolated ? 'yes' : 'no'} />
      <Row
        label="publishes"
        value={
          budget
            ? `${state.publishes.toLocaleString()} · ${state.publishHz.toFixed(1)} / ${budget.publishHz}/s`
            : `${state.publishes.toLocaleString()} · ${state.publishHz.toFixed(1)}/s`
        }
        // Not a threshold but an *invariant*: this rate may not move with the
        // load, whatever the crowd is doing (docs/13-architecture.md).
        tone={rateTone}
      />
      <Row label="per publish" value={kb(state.bytesPerPublish)} />
      <Row label="of which floor plane" value={kb(state.heatBytes)} />
      <Row
        label="throughput"
        value={
          budget
            ? `${kb(state.bytesPerSecond)}/s of ${budget.boundaryKbPerSecond} kB/s`
            : `${kb(state.bytesPerSecond)}/s`
        }
        tone={bytesTone}
      />
      <Row
        label="commands"
        value={
          state.commandsPerSecond > 0 || state.commandBytesPerSecond > 0
            ? `${state.commandsPerSecond.toFixed(1)}/s · ${kb(Math.round(state.commandBytesPerSecond))}/s`
            : 'idle'
        }
      />
      <Row label="ring" value={kb(state.ringBytes)} />
      <Row label="wasm memory" value={kb(state.wasmMemoryBytes)} />
      <Row
        label="commands dropped"
        value={String(state.droppedCommands)}
        tone={state.droppedCommands > 0 ? 'over' : undefined}
      />
    </section>
  );
}

function Sim({ state }: { state: HudState }) {
  const ui = state.ui;
  const worker = state.worker;
  return (
    <section>
      <h3>Site</h3>
      <Row
        label="theater time"
        value={ui ? `${theaterClock(ui.theaterSeconds)} @ ${state.tickHz} Hz` : '—'}
      />
      <Row
        label="ratio"
        value={ui ? `1 s = ${ratio(ui.tickHz * ui.theaterSecondsPerTick)} at 1×` : '—'}
      />
      <Row label="tick" value={ui ? ui.tick.toLocaleString() : '—'} />
      <Row label="speed" value={ui ? `${(ui.speedMille / 1000).toFixed(2)}×` : '—'} />
      <Row label="crew" value={ui ? `${ui.agents} / ${ui.capacity}` : '—'} />
      <Row
        label="worker step"
        value={
          worker && state.budget
            ? `${worker.stepMs.toFixed(3)} / ${(state.budget.workerStepUs / 1000).toFixed(3)} ms`
            : worker
              ? `${worker.stepMs.toFixed(3)} ms`
              : '—'
        }
        tone={
          worker && state.budget
            ? worker.stepMs * 1000 > state.budget.workerStepUs
              ? 'over'
              : 'ok'
            : undefined
        }
      />
      <Row
        label="worker publish"
        value={
          worker && state.budget
            ? `${worker.publishMs.toFixed(3)} / ${(state.budget.workerPublishUs / 1000).toFixed(3)} ms`
            : worker
              ? `${worker.publishMs.toFixed(3)} ms`
              : '—'
        }
        tone={
          worker && state.budget
            ? worker.publishMs * 1000 > state.budget.workerPublishUs
              ? 'over'
              : 'ok'
            : undefined
        }
      />
      <Row
        label="steps dropped"
        value={worker ? worker.droppedSteps.toLocaleString() : '—'}
        tone={worker && worker.droppedSteps > 0 ? 'warn' : undefined}
      />
      <Row label="commands" value={ui ? String(ui.commandsApplied) : '—'} />
      <Row label="state hash" value={ui ? ui.stateHash : '—'} mono />
      {/* M1.1: the glued hash — both clocks and the seam — beside the floor's,
          so a divergence anywhere in the campaign is visible live. */}
      <Row label="glued hash" value={state.campaign ? state.campaign.hash : '—'} mono />
      <Row
        label="calendar"
        value={
          state.campaign
            ? `${state.campaign.date} · ${state.campaign.pace} · ${state.campaign.watchedWeeks}w watched / ${state.campaign.forwardedWeeks}w forwarded`
            : '—'
        }
      />
    </section>
  );
}

function Controls({ host, state }: { host: FloorHost; state: HudState }) {
  const ui = state.ui;
  const speeds = [0.5, 1, 2, 4, 8];
  /**
   * Speeds nobody plays at, kept in their own row because they are a different
   * claim from the ones above. 8× is the design's watchable ceiling; these exist
   * because B0.5 made the prototype wait on things measured in theater *days* —
   * a calibration window is one, which is forty-five real minutes at 1× and
   * forty-five seconds at 64×. Above 8× the floor is a blur, and that is the
   * trade rather than a bug.
   */
  const testSpeeds = [16, 64, 128];
  // The authored shift, then the sizes B0.6 will stress the boundary at. The
  // same dial, because since B0.4 the crowd and the workforce are one crowd.
  const loads = [5, 30, 120, 512, 2048];
  // The starter fab tops out a little over five lots a theater day, so this
  // range walks it from idle to visibly drowning.
  const starts = [0, 2, 4, 5, 6, 8];
  return (
    <div className="controls">
      <div className="group">
        <button
          onClick={() => host.send({ kind: 'setPaused', paused: !(ui?.paused ?? false) })}
          className={ui?.paused ? 'active' : ''}
        >
          {ui?.paused ? 'Resume' : 'Pause'}
        </button>
        {speeds.map((speed) => (
          <button
            key={speed}
            className={ui && ui.speedMille === speed * 1000 ? 'active' : ''}
            onClick={() => host.send({ kind: 'setSpeed', mille: speed * 1000 })}
          >
            {speed}×
          </button>
        ))}
      </div>
      <div className="group">
        <span className="label">fast-forward</span>
        {testSpeeds.map((speed) => (
          <button
            key={speed}
            className={ui && ui.speedMille === speed * 1000 ? 'active' : ''}
            onClick={() => host.send({ kind: 'setSpeed', mille: speed * 1000 })}
          >
            {speed}×
          </button>
        ))}
        <span className="hint">
          past watchable — for reaching a calibration window, which is a theater day
        </span>
      </div>
      <div className="group">
        <span className="label">crew</span>
        <button onClick={() => host.send({ kind: 'setCrew', count: 0 })}>none</button>
        {loads.map((count) => (
          <button
            key={count}
            className={ui && ui.agents === count ? 'active' : ''}
            onClick={() => host.send({ kind: 'setCrew', count })}
          >
            {count}
          </button>
        ))}
        <span className="hint">the shift, and the boundary's load dial — one thing since B0.4</span>
      </div>
      <div className="group">
        <span className="label">transport</span>
        <a href="?transport=sab" className={state.transport === 'sab' ? 'active' : ''}>
          shared
        </a>
        <a href="?transport=copy" className={state.transport === 'copy' ? 'active' : ''}>
          copy
        </a>
        <button
          onClick={() => {
            const text = JSON.stringify({ report: state.report, hud: hudDigest(state) }, null, 2);
            void navigator.clipboard?.writeText(text);
          }}
        >
          Copy platform report
        </button>
      </div>
      <div className="group">
        <span className="label">starts/day</span>
        {starts.map((rate) => (
          <button
            key={rate}
            className={ui && ui.flow.startsPerDay === rate ? 'active' : ''}
            onClick={() => host.send({ kind: 'setStarts', lotsPerDay: rate })}
          >
            {rate}
          </button>
        ))}
        <button onClick={() => host.send({ kind: 'resetFlow' })}>clear WIP</button>
        <span className="hint">shift-click a lot to expedite it</span>
      </div>
      <div className="group">
        <button
          onClick={() => {
            const site = state.ui?.site;
            host.send({
              kind: 'callBreak',
              cellX: Math.floor((site?.plotW ?? 64) / 2),
              cellY: Math.floor((site?.plotH ?? 64) / 2),
              radiusCells: 96,
            });
          }}
        >
          Call a break
        </button>
        <span className="hint">everyone drops what they are holding — and nothing is lost</span>
      </div>
    </div>
  );
}

/** What a platform-matrix cell records beyond the environment probe. */
function hudDigest(state: HudState) {
  return {
    transport: state.transport,
    isolated: state.isolated,
    fps: Math.round(state.fps),
    frameMs: Number(state.frameMs.toFixed(2)),
    frameMsP95: Number(state.frameMsP95.toFixed(2)),
    // B0.6: the line the budget is actually about, and its parts.
    scriptMs: Number(state.scriptMs.toFixed(2)),
    scriptMsP95: Number(state.scriptMsP95.toFixed(2)),
    renderMs: Number(state.renderMs.toFixed(2)),
    boundaryMs: Number(state.boundaryMs.toFixed(2)),
    longTasksPerSecond: state.longTasksSupported ? Number(state.longTasks.toFixed(2)) : null,
    instances: state.drawn + state.structures + state.lots,
    agents: state.drawn,
    lod: state.lod,
    publishHz: Number(state.publishHz.toFixed(2)),
    bytesPerPublish: state.bytesPerPublish,
    bytesPerSecond: state.bytesPerSecond,
    commandBytesPerSecond: Math.round(state.commandBytesPerSecond),
    workerStepMs: state.worker ? Number(state.worker.stepMs.toFixed(3)) : null,
    workerPublishMs: state.worker ? Number(state.worker.publishMs.toFixed(3)) : null,
    droppedSteps: state.worker?.droppedSteps ?? null,
    droppedCommands: state.droppedCommands,
    budget: state.budget,
    stateHash: state.ui?.stateHash ?? null,
  };
}

function Row({
  label,
  value,
  tone,
  mono,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn' | 'over' | undefined;
  mono?: boolean | undefined;
}) {
  return (
    <div className="row">
      <span className="label">{label}</span>
      <span className={`value ${tone ?? ''} ${mono ? 'mono' : ''}`}>{value}</span>
    </div>
  );
}

/** Theater seconds as a shift clock — days and hours, not five-digit seconds. */
function theaterClock(seconds: number): string {
  const days = Math.floor(seconds / 86_400);
  const hours = (seconds % 86_400) / 3600;
  return days > 0 ? `${days}d ${hours.toFixed(1)}h` : `${hours.toFixed(2)}h`;
}

/** The theater-time ratio in the units a player thinks in. */
function ratio(theaterSecondsPerRealSecond: number): string {
  if (theaterSecondsPerRealSecond >= 3600) return `${(theaterSecondsPerRealSecond / 3600).toFixed(1)} h`;
  if (theaterSecondsPerRealSecond >= 60) return `${(theaterSecondsPerRealSecond / 60).toFixed(0)} min`;
  return `${theaterSecondsPerRealSecond} s`;
}

function kb(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
